import { createSelector } from 'reselect';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { UserStatus } from 'constants/telephony';
import { getIsNativePlatform } from 'utils/platform';

import { selectIsClaimAllowed } from 'store/auth/selectors/master.selectors';

import { getSystem } from './system.selectors';

const selectTelephony = createSelector(getSystem, ({ telephony }) => telephony);

export const selectTelephonyActiveTab = createSelector(
  selectTelephony,
  ({ activeTab }) => activeTab,
);

const selectTelephonyUser = createSelector(selectTelephony, ({ telephonyUser }) => telephonyUser);

const selectTelephonyUserData = createSelector(selectTelephonyUser, ({ user }) => user);

export const selectUserContactStatus = createSelector(
  selectTelephonyUserData,
  user => user?.contactStatus ?? UserStatus.UNAVAILABLE,
);

export const selectIsLoanOfficer = createSelector(
  selectTelephonyUserData,
  user => user?.isLoanOfficer ?? false,
);

export const selectIsTelephonyUserLoaded = createSelector(
  selectTelephonyUserData,
  user => user !== null,
);

// Telnyx connection selectors
const selectTelnyxState = createSelector(selectTelephony, ({ telnyx }) => telnyx);

export const selectIsTelnyxConnected = createSelector(
  selectTelnyxState,
  ({ isConnected }) => isConnected,
);

/**
 * Whether the current user can dial out right now: entitled to telephony, their telephony
 * profile is loaded, and a transport is available. Native carries its own always-on
 * connection, so only the web softphone has to be actually connected.
 */
export const selectCanPlaceCall = createSelector(
  selectIsClaimAllowed(COMPOSED_CLAIMS.TELEPHONY_ENABLED),
  selectIsTelephonyUserLoaded,
  selectIsTelnyxConnected,
  (isTelephonyEnabled, isTelephonyUserLoaded, isTelnyxConnected) =>
    isTelephonyEnabled && isTelephonyUserLoaded && (getIsNativePlatform() || isTelnyxConnected),
);

/**
 * Whether the current user can send a text right now — entitlement only.
 *
 * Unlike `selectCanPlaceCall` this waits on neither the Telnyx socket nor the telephony profile.
 * A text is sent by the REST API against the COMPANY's messaging profile: the request carries no
 * from-number, and Telnyx draws one from that company's number pool. The sender's own SIP
 * extension never takes part, so requiring their profile gated texting on something it does not
 * use — and because the profile is fetched only when the header telephony widget mounts, that read
 * to the user as "the phone has to be online before I can text" (DEV-6272).
 */
export const selectCanSendSms = selectIsClaimAllowed(COMPOSED_CLAIMS.TELEPHONY_ENABLED);

export const selectIsTelnyxConnecting = createSelector(
  selectTelnyxState,
  ({ isConnecting }) => isConnecting,
);

export const selectHasTelnyxEverConnected = createSelector(
  selectTelnyxState,
  ({ hasEverConnected }) => hasEverConnected,
);

// Call selectors
export const selectCallState = createSelector(selectTelephony, ({ call }) => call);

export const selectCallStatus = createSelector(selectCallState, ({ status }) => status);

export const selectDialedNumber = createSelector(
  selectTelephony,
  ({ dialedNumber }) => dialedNumber,
);

// Contacts selectors
const selectContactsState = createSelector(selectTelephony, ({ contacts }) => contacts);

const selectContacts = createSelector(selectContactsState, ({ list }) => list);

export const selectContactsLoading = createSelector(
  selectContactsState,
  ({ isLoading }) => isLoading,
);

export const selectContactsSearchQuery = createSelector(
  selectContactsState,
  ({ searchQuery }) => searchQuery,
);

export const selectFilteredContacts = createSelector(
  selectContacts,
  selectContactsSearchQuery,
  (contacts, searchQuery) => {
    if (!searchQuery) {
      return contacts;
    }

    const query = searchQuery.toLowerCase();

    return contacts.filter(contact => {
      const firstName = (contact.firstName ?? '').toLowerCase();
      const lastName = (contact.lastName ?? '').toLowerCase();
      const sipUsername = (contact.sipUsername ?? '').toLowerCase();
      const fullName = `${firstName} ${lastName}`;

      return (
        firstName.includes(query) ||
        lastName.includes(query) ||
        fullName.includes(query) ||
        sipUsername.includes(query)
      );
    });
  },
);

// Call History selectors
const selectCallHistoryState = createSelector(selectTelephony, ({ callHistory }) => callHistory);

const selectCallHistory = createSelector(selectCallHistoryState, ({ list }) => list);

export const selectCallHistoryLoading = createSelector(
  selectCallHistoryState,
  ({ isLoading }) => isLoading,
);

export const selectCallHistoryFilter = createSelector(
  selectCallHistoryState,
  ({ filter }) => filter,
);

export const selectFilteredCallHistory = createSelector(
  selectCallHistory,
  selectCallHistoryFilter,
  (history, filter) => {
    if (filter === 'all') {
      return history;
    }

    return history.filter(entry => {
      switch (filter) {
        case 'missed':
          return !entry.isAnswered;
        case 'inbound':
          return entry.typeOfCall === 'Inbound';
        case 'outbound':
          return entry.typeOfCall === 'Outbound';
        default:
          return true;
      }
    });
  },
);

// Lead notification selectors
export const selectLeadNotification = createSelector(
  selectTelephony,
  ({ leadNotification }) => leadNotification,
);

export const selectIsLeadNotificationVisible = createSelector(
  selectLeadNotification,
  ({ isVisible }) => isVisible,
);

export const selectLeadNotificationId = createSelector(
  selectLeadNotification,
  ({ notificationId }) => notificationId,
);

export const selectLeadData = createSelector(selectLeadNotification, ({ leadData }) => leadData);

export const selectIsLeadLiveTransfer = createSelector(
  selectLeadNotification,
  ({ isLiveTransfer }) => isLiveTransfer,
);

export const selectIsAcceptingLead = createSelector(
  selectLeadNotification,
  ({ isAccepting }) => isAccepting,
);

export const selectIsRejectingLead = createSelector(
  selectLeadNotification,
  ({ isRejecting }) => isRejecting,
);

// Text messaging selectors (DEV-1453)
const selectTextMessaging = createSelector(selectTelephony, ({ textMessaging }) => textMessaging);

const selectTextLeadsState = createSelector(selectTextMessaging, ({ leads }) => leads);

export const selectTextLeadsList = createSelector(selectTextLeadsState, ({ list }) => list);

export const selectTextLeadsLoading = createSelector(
  selectTextLeadsState,
  ({ isLoading }) => isLoading,
);

export const selectTextLeadsSearchQuery = createSelector(
  selectTextLeadsState,
  ({ searchQuery }) => searchQuery,
);

const selectActiveConversation = createSelector(
  selectTextMessaging,
  ({ activeConversation }) => activeConversation,
);

export const selectActiveConversationLead = createSelector(
  selectActiveConversation,
  ({ lead }) => lead,
);

export const selectActiveConversationMessages = createSelector(
  selectActiveConversation,
  ({ messages }) => messages,
);

export const selectIsConversationLoading = createSelector(
  selectActiveConversation,
  ({ isLoading }) => isLoading,
);

export const selectIsSendingMessage = createSelector(
  selectActiveConversation,
  ({ isSending }) => isSending,
);

// Borrower chat selectors (DEV-1454)
const selectBorrowerChat = createSelector(selectTelephony, ({ borrowerChat }) => borrowerChat);

export const selectBorrowerChatActiveChannel = createSelector(
  selectBorrowerChat,
  ({ activeChannelUrl }) => activeChannelUrl,
);

export const selectBorrowerChatUnreadCount = createSelector(
  selectBorrowerChat,
  ({ unreadCount }) => unreadCount,
);

// Internal chat selectors (DEV-1455)
const selectInternalChat = createSelector(selectTelephony, ({ internalChat }) => internalChat);

export const selectInternalChatActiveChannel = createSelector(
  selectInternalChat,
  ({ activeChannelUrl }) => activeChannelUrl,
);

export const selectInternalChatUnreadCount = createSelector(
  selectInternalChat,
  ({ unreadCount }) => unreadCount,
);

// SignalR connection selectors
const selectSignalRState = createSelector(selectTelephony, ({ signalR }) => signalR);

export const selectIsSignalRReconnecting = createSelector(
  selectSignalRState,
  ({ isReconnecting }) => isReconnecting,
);

const selectIsSignalRConnected = createSelector(
  selectSignalRState,
  ({ isConnected }) => isConnected,
);

// Connection health selectors
const selectTelnyxError = createSelector(selectTelnyxState, ({ error }) => error);

const selectTelnyxConnectionSummary = createSelector(
  selectIsTelnyxConnected,
  selectIsTelnyxConnecting,
  selectHasTelnyxEverConnected,
  selectTelnyxError,
  (isConnected, isConnecting, hasEverConnected, error) => ({
    isConnected,
    isConnecting,
    hasEverConnected,
    error,
  }),
);

export const selectConnectionHealthStatus = createSelector(
  selectTelnyxConnectionSummary,
  selectIsSignalRConnected,
  selectIsSignalRReconnecting,
  (telnyx, signalRConnected, signalRReconnecting) => {
    if (telnyx.error) {
      return { hasError: true, message: `Telephony error: ${telnyx.error}` };
    }

    // Don't show error banners during initial connection — services haven't had time to connect yet.
    if (!telnyx.hasEverConnected && (telnyx.isConnecting || !signalRConnected)) {
      return { hasError: false, message: null };
    }

    if (!telnyx.isConnected && !signalRConnected) {
      return { hasError: true, message: 'Not connected to telephony service' };
    }

    if (!telnyx.isConnected) {
      return { hasError: true, message: 'Phone service disconnected' };
    }

    if (signalRReconnecting) {
      return { hasError: false, message: 'Reconnecting...' };
    }

    if (!signalRConnected) {
      return { hasError: true, message: 'Real-time events disconnected' };
    }

    return { hasError: false, message: null };
  },
);

// Audio device settings selectors (DEV-1482)
const selectAudioDeviceSettings = createSelector(
  selectTelephony,
  ({ audioDeviceSettings }) => audioDeviceSettings,
);

export const selectSelectedInputDeviceId = createSelector(
  selectAudioDeviceSettings,
  ({ selectedInputDeviceId }) => selectedInputDeviceId,
);

export const selectSelectedOutputDeviceId = createSelector(
  selectAudioDeviceSettings,
  ({ selectedOutputDeviceId }) => selectedOutputDeviceId,
);

export const selectAvailableInputDevices = createSelector(
  selectAudioDeviceSettings,
  ({ availableInputDevices }) => availableInputDevices,
);

export const selectAvailableOutputDevices = createSelector(
  selectAudioDeviceSettings,
  ({ availableOutputDevices }) => availableOutputDevices,
);

export const selectAudioPermissionState = createSelector(
  selectAudioDeviceSettings,
  ({ permissionState }) => permissionState,
);

// Voicemail greeting selectors (DEV-1575)
const selectVoicemailGreeting = createSelector(
  selectTelephony,
  ({ voicemailGreeting }) => voicemailGreeting,
);

export const selectActiveGreetingId = createSelector(
  selectVoicemailGreeting,
  ({ activeGreetingId }) => activeGreetingId,
);

export const selectIsGreetingLoading = createSelector(
  selectVoicemailGreeting,
  ({ isLoading }) => isLoading,
);

export const selectIsGreetingSaving = createSelector(
  selectVoicemailGreeting,
  ({ isSaving }) => isSaving,
);

// Voicemail inbox selectors (DEV-1577)
const selectVoicemailInbox = createSelector(
  selectTelephony,
  ({ voicemailInbox }) => voicemailInbox,
);

export const selectVoicemails = createSelector(selectVoicemailInbox, ({ list }) => list);

export const selectVoicemailUnreadCount = createSelector(
  selectVoicemailInbox,
  ({ unreadCount }) => unreadCount,
);

export const selectVoicemailsLoading = createSelector(
  selectVoicemailInbox,
  ({ isLoading }) => isLoading,
);
